import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Lock, Mail, KeyRound, AlertCircle } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const resp = await login({ email, password, mfa_code: mfaCode });
      if (resp.must_change_password) {
        navigate("/conta/senha?force=1", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Falha no login. Verifique credenciais e MFA.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative grid lg:grid-cols-5 bg-background text-foreground overflow-hidden"
      data-testid="login-page"
    >
      <div className="hidden lg:flex lg:col-span-3 relative items-end p-12">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1768101225267-c6fc5678c113?crop=entropy&cs=srgb&fm=jpg&q=85')",
            filter: "saturate(0.85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-transparent" />
        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground mb-3">
            SoIIN · v1.0
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Inteligência de mercado para decisões transparentes.
          </h1>
          <p className="mt-5 text-muted-foreground max-w-sm text-sm leading-relaxed">
            Capture EOD diário da B3, NYSE e NASDAQ. Score técnico em 5 janelas,
            notas analíticas com fontes verificáveis e isolamento total dos seus favoritos.
          </p>
        </div>
      </div>

      <div className="col-span-2 flex items-center justify-center p-6 sm:p-10 bg-sidebar border-l border-sidebar-border">
        <Card className="w-full max-w-md border-sidebar-border shadow-none bg-transparent">
          <CardHeader className="px-0">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-md bg-sidebar-primary text-sidebar-primary-foreground flex items-center justify-center font-display font-bold">
                S
              </div>
              <div>
                <div className="font-display font-bold tracking-tight">SoIIN</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Stock Intelligence
                </div>
              </div>
            </div>
            <CardTitle className="font-display text-2xl tracking-tight">Acessar conta</CardTitle>
            <CardDescription>Sistema restrito. Acesso somente para usuários cadastrados.</CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    placeholder="seu@email.com"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mfa">Código MFA (6 dígitos)</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="mfa"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    className="pl-9 font-mono-tab tracking-[0.4em]"
                    placeholder="000000"
                    data-testid="login-mfa-input"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                  data-testid="login-error"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Entrando…" : "Entrar"}
              </Button>

              <p className="text-[11px] text-muted-foreground text-center">
                Não é possível auto cadastrar. Solicite acesso ao administrador.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
